import { useEffect, useState } from "react"

import { Button, Form } from "@/atoms"
import { hasTicketProjects } from "@/domain/ticketing"
import { buildTicketFields } from "@/helpers/ticketing.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { EditOutlined, EyeOutlined } from "@/icons"
import {
  filterOnDemandFieldsWithValues,
  processValidationErrors,
  toServerErrors
} from "@/organisms/Ticketing/components/JiraTemplateModal/helpers"
import { TicketFields } from "@/organisms/Ticketing/components/TicketField"
import { TicketModal } from "@/organisms/Ticketing/components/TicketModal"
import { TicketTestModal } from "@/organisms/Ticketing/components/TicketTestModal"
import { useServerFieldErrors } from "@/organisms/Ticketing/hooks/useServerFieldErrors"
import { useTemplateTest } from "@/organisms/Ticketing/hooks/useTemplateTest"
import { useVendorTicketingMetadata } from "@/organisms/Ticketing/hooks/useVendorTicketingMetadata"
import { getInitialTemplateValues } from "./TicketTemplateModal.helpers"

import type { TicketFormValues } from "@/helpers/ticketing.helpers.types"
import type { TicketTemplateModalProps } from "./TicketTemplateModal.types"

export const TicketTemplateModal = (props: TicketTemplateModalProps) => {
  const { system, open, closeModal, existingTemplate, extra } = props
  const { validationErrors, testConfig, hasWritePermissions } = props
  const { t } = useTranslation("translation", {
    keyPrefix: "ticketModal"
  })
  const [form] = Form.useForm<TicketFormValues>()
  const hasProjects = hasTicketProjects(system)
  const [initialValues] = useState(() =>
    getInitialTemplateValues(existingTemplate, extra)
  )
  const [initialErrors] = useState(() =>
    validationErrors ? processValidationErrors(validationErrors, t) : null
  )
  const { setServerErrors, createServerErrorRule, getServerErrorFields } =
    useServerFieldErrors(
      toServerErrors(initialErrors?.fieldErrors, initialValues)
    )
  const [formError, setFormError] = useState<string | null>(
    initialErrors?.formErrors.join("; ") || null
  )
  const [previewMode, setPreviewMode] = useState(!hasWritePermissions)
  const { testModalState, setTestModalState, confirmTest } = useTemplateTest({
    form,
    t,
    setFormError,
    setServerErrors,
    // Without a project there is nowhere to create a test ticket
    validatesOnly: !hasProjects,
    onEmptyResult: () => {
      setFormError(t("error.creationFailed"))
      setTestModalState(null)
    }
  })
  const metadata = useVendorTicketingMetadata(system, form, {
    initialProjectId: initialValues.project,
    initialTypeId: initialValues.type
  })
  const { projectId, projects, types, fields, url, fieldsIsLoading } = metadata

  // Saved errors show on their fields once the vendor's fields are rendered
  useEffect(() => {
    const names = getServerErrorFields()

    if (!fieldsIsLoading && fields.length && names.length)
      form.validateFields(names).catch(() => {})
  }, [form, fields, fieldsIsLoading, getServerErrorFields])

  const getTemplate = () => {
    const values = form.getFieldsValue(true)

    return {
      fields: buildTicketFields(fields, values),
      onDemandFields: filterOnDemandFieldsWithValues(values.onDemandFields)
    }
  }

  const handleFieldsChange = () => {
    const hasErrors =
      getServerErrorFields().length > 0 ||
      form.getFieldsError().some(({ errors }) => errors.length)

    if (!hasErrors) setFormError(null)
  }

  const handleSubmit = async () => {
    try {
      await form.validateFields()
    } catch {
      setFormError(t("error.completeRequiredFields"))
      return
    }

    if (testConfig) setTestModalState("open")
    else closeModal(getTemplate())
  }

  const handleConfirmTest = () => {
    if (!testConfig) return

    return confirmTest(
      () => testConfig.onTest(getTemplate()),
      form.getFieldsValue(true)
    )
  }

  return (
    <>
      <TicketModal
        system={system}
        type={open ? system : undefined}
        mode="template"
        form={form}
        initialValues={initialValues}
        fixedProjectLabel={hasProjects ? undefined : t("repositoryField")}
        url={url}
        projects={projects}
        issueTypesOptions={types}
        metadataIsLoading={metadata.metadataIsLoading}
        fieldsIsLoading={fieldsIsLoading}
        formError={formError ?? metadata.unavailableMessage ?? null}
        initialSummary={initialValues.title}
        initialDescription={initialValues.description}
        tags={extra?.dynamicTags}
        createServerErrorRule={createServerErrorRule}
        submitDisabled={fieldsIsLoading}
        previewMode={previewMode}
        setPreviewMode={setPreviewMode}
        onFieldsChange={handleFieldsChange}
        onCancel={() => closeModal()}
        onSubmit={handleSubmit}
        footer={
          <Button
            disabled={!hasWritePermissions}
            type="link"
            icon={previewMode ? <EditOutlined /> : <EyeOutlined />}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {t(`mode.${previewMode ? "edit" : "preview"}`)}
          </Button>
        }
      >
        <TicketFields
          system={system}
          mode="template"
          form={form}
          projectId={projectId}
          fields={fields}
          tags={extra?.dynamicTags}
          disabled={previewMode}
          previewMode={previewMode}
          initialValues={initialValues}
          createServerErrorRule={createServerErrorRule}
        />
      </TicketModal>
      <TicketTestModal
        system={system}
        state={testModalState}
        isLoading={!!testConfig?.isLoading}
        onClose={() => setTestModalState(null)}
        onConfirmTest={handleConfirmTest}
        onSuccessOk={() => {
          setTestModalState(null)
          closeModal(getTemplate())
        }}
      />
    </>
  )
}
